import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { TileHeader, ConditionalContent, Tile } from '../../detail/tabs/Tile';
import { ConsultationContentType, EXPECTED_OWNING_PATIENT_ERRORS } from '../../detail/tabs/contentTypes';
import { ConsultationService, sharedApiClient } from '../../fetcher';
import { useAuth, useNotifications } from '@medprompt/shared';
import { UserAbility } from '@medprompt/shared';
import { ConsultationStatusEnum } from '../../enums/consultationStatusEnum';
import { getNotesModeDisplay } from '../notesModeEnum';
import { EncounterType } from '../../enums/encounterTypeEnum';
import { FeatureFlags } from '../../FeatureFlags';

const consultationShouldShowContent = (field, status) => {
  return field !== undefined || status === ConsultationStatusEnum.READY.value;
};

// Tiles for fields that are not generated (NHS is user-edited; settings are not a ConsultationContentType)
const StaticTile = ({
  className,
  title,
  condition,
  children
}) => (
  <div className={`tile ${className || ''}`}>
    <TileHeader
      title={title}
      isEnabled={false}
    />
    <ConditionalContent condition={condition}>
      {children}
    </ConditionalContent>
  </div>
);

const DetailsTab = ({
  consultation,
  patientData,
  patientRecordData,
  isEditable,
  onConsultationUpdate,
  isRegenerationEnabled,
  handleRegenerationClick,
  regenerateService
}) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const hasConsultWriteAbility = user?.authorities?.includes(UserAbility.CONSULT_WRITE.key);
  const canEdit = isEditable && hasConsultWriteAbility;

  // State for NHS ID inline editing
  const [isEditingNhsId, setIsEditingNhsId] = useState(false);
  const [editableNhsId, setEditableNhsId] = useState('');
  const [nhsIdError, setNhsIdError] = useState('');
  const nhsIdInputRef = useRef(null);
  
  // State for templates
  const [templates, setTemplates] = useState([]);
  
  const consultationService = useMemo(() => new ConsultationService(sharedApiClient), []);

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const fetchedTemplates = await consultationService.fetchNotesTemplates();
        setTemplates(fetchedTemplates || []);
      } catch (err) {
        console.error('Error fetching notes templates:', err);
      }
    };
    fetchTemplates();
  }, [consultationService]);

  // Update editable NHS ID when consultation changes (but only when not editing)
  useEffect(() => {
    if (!isEditingNhsId) {
      setEditableNhsId(consultation?.nhsId || '');
    }
  }, [consultation?.nhsId, isEditingNhsId]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingNhsId && nhsIdInputRef.current) {
      nhsIdInputRef.current.focus();
      nhsIdInputRef.current.select();
    }
  }, [isEditingNhsId]);

  // Copy to clipboard function
  const handleCopyToClipboard = useCallback((text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        addNotification(`copy-success-${Date.now()}`, 'Copied to clipboard', { isError: false });
      }).catch(() => {
        addNotification(`copy-error-${Date.now()}`, 'Failed to copy to clipboard', { isError: true });
      });
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        addNotification(`copy-success-${Date.now()}`, 'Copied to clipboard', { isError: false });
      } catch (err) {
        addNotification(`copy-error-${Date.now()}`, 'Failed to copy to clipboard', { isError: true });
      }
      document.body.removeChild(textArea);
    }
  }, [addNotification]);

  // NHS ID editing handlers
  const handleNhsIdClick = useCallback(() => {
    if (!canEdit) return;
    setIsEditingNhsId(true);
    setNhsIdError('');
  }, [canEdit]);

  const handleNhsIdBlur = useCallback(() => {
    if (!editableNhsId.trim()) {
      setIsEditingNhsId(false);
      setEditableNhsId(consultation?.nhsId || '');
      setNhsIdError('');
      return;
    }

    const nhsIdRegex = /^\d{3}[ -]?\d{3}[ -]?\d{4}$/;
    
    if (!nhsIdRegex.test(editableNhsId)) {
      setNhsIdError('NHS Number must be in format: 123 456 7890');
      return;
    }
    
    const cleanNhsId = editableNhsId.replace(/[\s-]/g, '');
    setNhsIdError('');
    setIsEditingNhsId(false);
    
    // Auto-save if changed
    if (cleanNhsId !== consultation?.nhsId) {
      consultationService.updateConsultation(consultation.consultationId, { nhsId: cleanNhsId })
        // Merges into the current consultation, not one captured at render: this runs once the
        // save resolves, by which point the update stream may already have replaced it. The id
        // check covers the save outliving a move to a different consultation.
        .then(() => onConsultationUpdate?.(prev => (
          prev?.consultationId === consultation.consultationId
            ? { ...prev, nhsId: cleanNhsId }
            : prev
        )))
        .catch((error) => {
          console.error('Error updating NHS Number:', error);
          addNotification(`nhs-id-error-${consultation.consultationId}`, 'Failed to update NHS Number', { isError: true });
          setEditableNhsId(consultation?.nhsId || '');
        });
    }
  }, [editableNhsId, consultation?.nhsId, consultation?.consultationId, consultationService, addNotification, onConsultationUpdate]);

  const handleNhsIdKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setEditableNhsId(consultation?.nhsId || '');
      setIsEditingNhsId(false);
      setNhsIdError('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      nhsIdInputRef.current?.blur();
    }
  }, [consultation?.nhsId]);

  // Extract NHS ID content
  const renderNhsIdContent = () => {
    const formattedNhsId = editableNhsId && editableNhsId.length === 10 
      ? `${editableNhsId.slice(0, 3)} ${editableNhsId.slice(3, 6)} ${editableNhsId.slice(6)}`
      : editableNhsId;

    return (
      <div className="patient-details detected-info">
        <div className="document-detail-row nhs-number-detail-row">
          <span className="document-detail-label nhs-number-detail-label">NHS Number:</span>
          <div className="detail-value-with-controls">
            {isEditingNhsId ? (
              <div className="nhs-id-inline-edit-container">
                <input
                  ref={nhsIdInputRef}
                  type="text"
                  className={`nhs-id-inline-input ${nhsIdError ? 'nhs-input-error' : ''}`}
                  value={editableNhsId}
                  onChange={(e) => setEditableNhsId(e.target.value)}
                  onBlur={handleNhsIdBlur}
                  onKeyDown={handleNhsIdKeyDown}
                  placeholder="XXX XXX XXXX"
                />
                {nhsIdError && <div className="nhs-input-error-message">{nhsIdError}</div>}
              </div>
            ) : (
              <>
                <span 
                  className={`document-detail-value nhs-number-detail-value ${canEdit ? 'detail-value-editable' : ''}`}
                  onClick={handleNhsIdClick}
                  onMouseMove={(e) => {
                    const tooltip = e.currentTarget.querySelector('.edit-tooltip');
                    if (tooltip) {
                      tooltip.style.left = `${e.clientX + 10}px`;
                      tooltip.style.top = `${e.clientY + 10}px`;
                    }
                  }}
                >
                  {formattedNhsId || <em>not found</em>}
                  {canEdit && <div className="edit-tooltip">Click to edit NHS Number</div>}
                </span>
                {editableNhsId && (
                  <button 
                    className="button copy-button" 
                    onClick={() => handleCopyToClipboard(editableNhsId)}
                    title="Copy NHS number"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Extract patient info content
  const renderPatientInfoContent = () => {
    if (!consultation?.patientId) {
      if (consultation?.status === ConsultationStatusEnum.GENERATING.value) {
        return null;
      } else {
        return (
          <div className="patient-details detected-info">
            <div className="empty-state-container">
              <div className="empty-state-message">NHS number not matched to patient</div>
            </div>
          </div>
        );
      }
    }

    const patient = patientData || patientRecordData?.patient;

    if (!patient) {
      return (
        <div className="patient-details detected-info">
          <div className="data-loading-container">
            <div className="data-loading-spinner"></div>
            <div className="data-loading-text">Loading patient data...</div>
          </div>
        </div>
      );
    }

    // Helper to calculate age
    const calculateAge = (dob) => {
      if (!dob) return null;
      try {
        const [day, month, year] = dob.split('/').map(Number);
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return age >= 0 ? `${age}y` : null;
      } catch {
        return null;
      }
    };
    return (
      <div className="patient-details detected-info">
        <div className="document-detail-row">
          <span className="document-detail-label">Name:</span>
          <span className="document-detail-value">{patient.name || <em>not found</em>}</span>
        </div>
        <div className="document-detail-row">
          <span className="document-detail-label">Age:</span>
          <span className="document-detail-value">{calculateAge(patient.dateOfBirth) || <em>not found</em>}</span>
        </div>
        <div className="document-detail-row">
          <span className="document-detail-label">Sex:</span>
          <span className="document-detail-value">{patient.sex || <em>not found</em>}</span>
        </div>
      </div>
    );
  };

  // Helper to resolve templateId to display name
  const getTemplateDisplay = useCallback((templateId) => {
    if (!templateId) return <em>not set</em>;
    const template = templates.find(t => t.templateId === templateId);
    return template ? template.title : 'Unknown Template';
  }, [templates]);

  const currentEncounterType = consultation?.consultationDetails?.encounterType;
  const isHiddenFromPatient = consultation?.consultationDetails?.isHiddenFromPatient ?? false;
  const isPatientFacingVisible = !isHiddenFromPatient;

  const handlePatientFacingVisibilityChange = useCallback((checked) => {
    if (!canEdit || !consultation?.consultationId) {
      return;
    }

    const nextIsHiddenFromPatient = !checked;
    const previousDetails = consultation.consultationDetails;
    onConsultationUpdate?.({
      ...consultation,
      consultationDetails: {
        ...previousDetails,
        isHiddenFromPatient: nextIsHiddenFromPatient,
      },
    });

    consultationService.updateConsultationDetails(consultation.consultationId, { isHiddenFromPatient: nextIsHiddenFromPatient })
      .catch((error) => {
        console.error('Error updating patient facing visibility:', error);
        addNotification(
          `patient-facing-visibility-error-${consultation.consultationId}`,
          'Failed to update patient facing visibility',
          { isError: true }
        );
        onConsultationUpdate?.({
          ...consultation,
          consultationDetails: previousDetails,
        });
      });
  }, [
    canEdit,
    consultation,
    consultationService,
    onConsultationUpdate,
    addNotification,
  ]);

  const handleEncounterTypeChange = useCallback((encounterType) => {
    if (!canEdit || !consultation?.consultationId || encounterType === currentEncounterType) {
      return;
    }

    const previousDetails = consultation.consultationDetails;
    onConsultationUpdate?.({
      ...consultation,
      consultationDetails: {
        ...previousDetails,
        encounterType,
      },
    });

    consultationService.updateConsultationDetails(consultation.consultationId, { encounterType })
      .catch((error) => {
        console.error('Error updating encounter type:', error);
        addNotification(
          `encounter-type-error-${consultation.consultationId}`,
          'Failed to update consultation type',
          { isError: true }
        );
        // Revert only this field against the current consultation: spreading the one captured
        // when the dropdown was clicked would discard anything that arrived in the meantime.
        onConsultationUpdate?.(prev => (prev?.consultationId === consultation.consultationId
          ? { ...prev, consultationDetails: previousDetails }
          : prev));
      });
  }, [
    canEdit,
    consultation,
    currentEncounterType,
    consultationService,
    onConsultationUpdate,
    addNotification,
  ]);

  // Render consultation info
  const renderConsultationInfo = () => {
    return (
      <div className="patient-details">
        {FeatureFlags.PATIENT_FACING_VISIBILITY_TOGGLE && (
          <div className="details-section">
            <div className="info-row patient-facing-visibility-row">
              <label className="fileaway-checkbox-label">
                <input
                  type="checkbox"
                  checked={isPatientFacingVisible}
                  onChange={(e) => handlePatientFacingVisibilityChange(e.target.checked)}
                  disabled={!canEdit}
                />
                <span>Display on patient's online record</span>
              </label>
            </div>
          </div>
        )}
        <div className="document-detail-row">
          <span className="document-detail-label">Template:</span>
          <span className="document-detail-value">
            {getTemplateDisplay(consultation?.templateId)}
          </span>
        </div>
        <div className="document-detail-row">
          <span className="document-detail-label">Detail Level:</span>
          <span className="document-detail-value">{getNotesModeDisplay(consultation?.notesMode || 'MEDIUM')}</span>
        </div>
        <div className="document-detail-row encounter-type-detail-row">
          <span className="document-detail-label">Consultation type:</span>
          <div
            className="details-document-type-toggle"
            role="radiogroup"
            aria-label="Consultation type"
          >
            {Object.values(EncounterType).map((type) => (
              <button
                key={type.value}
                type="button"
                role="radio"
                aria-checked={currentEncounterType === type.value}
                className={`details-document-type-toggle-btn ${currentEncounterType === type.value ? 'is-selected' : ''}`}
                onClick={() => handleEncounterTypeChange(type.value)}
                disabled={!canEdit}
              >
                {type.displayName}
              </button>
            ))}
          </div>
        </div>
        {consultation?.timestamp && (
          <div className="document-detail-row">
            <span className="document-detail-label">Created:</span>
            <span className="document-detail-value">{new Date(consultation.timestamp).toLocaleString()}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="document-detail-tiles">
      <StaticTile
        title="NHS Number"
        condition={consultationShouldShowContent(consultation?.nhsId, consultation?.status)}
      >
        {renderNhsIdContent()}
      </StaticTile>

      <Tile
        doc={consultation}
        entityId={consultation?.consultationId}
        contentType={ConsultationContentType.OWNING_PATIENT_STATUS}
        title="Patient Details"
        condition={consultation?.patientId || consultation?.status !== ConsultationStatusEnum.GENERATING.value}
        isEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false}
        onRegenerateClick={handleRegenerationClick}
        regenerateService={regenerateService}
        showContentOnError
        shouldShowError={(error) => !consultation?.patientId && !EXPECTED_OWNING_PATIENT_ERRORS.has(error.message)}
      >
        {renderPatientInfoContent()}
      </Tile>

      <StaticTile
        title="Consultation Settings"
        condition={true}
      >
        {renderConsultationInfo()}
      </StaticTile>
    </div>
  );
};

export default DetailsTab;
